import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { CustomerNameInput } from './CustomerPicker';
import './UploadReceipt.css';

function localIsoDate() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

function CreateInvoice() {
  const navigate = useNavigate();
  const today = localIsoDate();

  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(today);
  const [deliveryDate, setDeliveryDate] = useState(today);
  const [customerName, setCustomerName] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [customerVatId, setCustomerVatId] = useState('');
  const [leistung, setLeistung] = useState('');
  const [betrag, setBetrag] = useState('');
  const [mwstOption, setMwstOption] = useState('none'); // 'none' | '19' | '7'
  const [amountType, setAmountType] = useState('gross'); // 'gross' | 'net'
  const [alreadyPaid, setAlreadyPaid] = useState(false);
  const [paidDate, setPaidDate] = useState(today);
  const [paymentMethod, setPaymentMethod] = useState('bar'); // 'bar' | 'card'
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [saveAsCustomer, setSaveAsCustomer] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/api/customers')
      .then((response) => setCustomers(response.data.customers || []))
      .catch(() => {});
  }, []);

  const buyerIdentificationWarning =
    !customerVatId.trim() && !customerAddress.trim()
      ? 'Rechnungsempfänger ohne USt-IdNr und ohne Adresse — für eine gültige B2B-Rechnung sollte mindestens die Anschrift vorhanden sein.'
      : '';

  const parsedAmount = parseFloat(betrag) || 0;

  const computeTotals = () => {
    if (mwstOption === 'none') {
      return { netAmount: parsedAmount, vatRate: 0, vatAmount: 0, grossAmount: parsedAmount };
    }
    const rate = parseInt(mwstOption);
    if (amountType === 'gross') {
      const net = parsedAmount / (1 + rate / 100);
      const vat = parsedAmount - net;
      return { netAmount: net, vatRate: rate, vatAmount: vat, grossAmount: parsedAmount };
    } else {
      const vat = parsedAmount * (rate / 100);
      const gross = parsedAmount + vat;
      return { netAmount: parsedAmount, vatRate: rate, vatAmount: vat, grossAmount: gross };
    }
  };

  const { netAmount, vatRate, vatAmount, grossAmount } = computeTotals();

  const applyCustomer = (id, customer) => {
    setSelectedCustomerId(id);
    if (customer) {
      setCustomerName(customer.name || '');
      setCustomerAddress(customer.address || '');
      setCustomerVatId(customer.vat_id || '');
      setSaveAsCustomer(false);
    }
  };

  const handleSubmit = async () => {
    if (!customerName.trim()) {
      setError('Bitte Kundenname eingeben.');
      return;
    }
    if (!leistung.trim()) {
      setError('Bitte Leistungsbeschreibung eingeben.');
      return;
    }
    if (!betrag || parsedAmount <= 0) {
      setError('Bitte einen gültigen Betrag eingeben.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const unitPrice = mwstOption === 'none'
        ? parsedAmount
        : amountType === 'net' ? parsedAmount : netAmount;

      const payload = {
        invoice_date: invoiceDate,
        delivery_date: deliveryDate,
        customer_name: customerName,
        customer_address: customerAddress,
        customer_vat_id: customerVatId.trim() || undefined,
        items: [
          {
            quantity: 1,
            description: leistung,
            unit_price: unitPrice,
            total_price: unitPrice,
            vat_rate: mwstOption === 'none' ? 0 : parseInt(mwstOption),
          }
        ],
        net_amount: netAmount,
        vat_rate: vatRate,
        vat_amount: vatAmount,
        gross_amount: grossAmount,
        vat_details: mwstOption === 'none'
          ? [{ rate: 0, net_amount: parsedAmount, vat_amount: 0, gross_amount: parsedAmount }]
          : [{
              rate: vatRate,
              net_amount: netAmount,
              vat_amount: vatAmount,
              gross_amount: grossAmount,
            }],
        tip_amount: 0,
        already_paid: alreadyPaid,
        paid_date: alreadyPaid ? paidDate : null,
        payment_method: alreadyPaid ? paymentMethod : null,
        invoice_number: invoiceNumber ? parseInt(invoiceNumber) : undefined,
      };

      await api.post('/api/invoices', payload);

      if (saveAsCustomer && !selectedCustomerId) {
        try {
          await api.post('/api/customers', {
            name: customerName,
            address: customerAddress,
            vat_id: customerVatId,
          });
        } catch (saveErr) {
          alert(saveErr.response?.data?.error || 'Rechnung erstellt, Kunde konnte aber nicht gespeichert werden.');
        }
      }

      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Erstellen der Rechnung.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <header className="header">
        <div className="header-content">
          <h1>Rechnung erstellen</h1>
          <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
            Zurück zum Dashboard
          </button>
        </div>
      </header>

      <div className="container">
        <div className="card">
          <h2>Neue Rechnung</h2>

          {/* Invoice Meta */}
          <div className="form-section">
            <h3>Rechnungsdetails</h3>
            <div className="form-row">
              <div className="form-group">
                <label className="label">Rechnungsnummer (optional)</label>
                <input
                  type="number"
                  className="input"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  placeholder="Wird automatisch vergeben"
                />
              </div>
              <div className="form-group">
                <label className="label">Rechnungsdatum</label>
                <input
                  type="date"
                  className="input"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="label">Leistungsdatum</label>
                <input
                  type="date"
                  className="input"
                  value={deliveryDate}
                  onChange={(e) => setDeliveryDate(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Customer */}
          <div className="form-section">
            <h3>Rechnungsempfänger</h3>
            <CustomerNameInput
              id="create-invoice-customer-name"
              value={customerName}
              customers={customers}
              required
              onChange={(name) => {
                setCustomerName(name);
                setSelectedCustomerId('');
              }}
              onSelectCustomer={applyCustomer}
            />
            <div className="form-group">
              <label className="label">Adresse (optional)</label>
              <textarea
                className="input"
                rows="3"
                value={customerAddress}
                onChange={(e) => {
                  setCustomerAddress(e.target.value);
                  setSelectedCustomerId('');
                }}
                placeholder="Straße, PLZ Ort"
              />
            </div>
            <div className="form-group">
              <label className="label">USt-IdNr (Rechnungsempfänger)</label>
              <input
                type="text"
                className="input"
                value={customerVatId}
                onChange={(e) => {
                  setCustomerVatId(e.target.value);
                  setSelectedCustomerId('');
                }}
                placeholder="z.B. DE123456789"
              />
            </div>
            {!selectedCustomerId && (
              <label className="checkbox-row save-customer-row">
                <input
                  type="checkbox"
                  checked={saveAsCustomer}
                  onChange={(e) => setSaveAsCustomer(e.target.checked)}
                />
                <span>Diesen Empfänger als Kunden speichern</span>
              </label>
            )}
          </div>

          {/* Service & Amount */}
          <div className="form-section">
            <h3>Leistung & Betrag</h3>
            <div className="form-group">
              <label className="label">Leistungsbeschreibung *</label>
              <input
                type="text"
                className="input"
                value={leistung}
                onChange={(e) => setLeistung(e.target.value)}
                placeholder="z.B. Eventbewirtung, Catering, Miete, ..."
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="label">Betrag (EUR) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="input"
                  value={betrag}
                  onChange={(e) => setBetrag(e.target.value)}
                  placeholder="0.00"
                  required
                />
              </div>

              <div className="form-group">
                <label className="label">MwSt.</label>
                <select
                  className="input"
                  value={mwstOption}
                  onChange={(e) => setMwstOption(e.target.value)}
                >
                  <option value="none">Ohne MwSt.</option>
                  <option value="19">19% MwSt.</option>
                  <option value="7">7% MwSt.</option>
                </select>
              </div>

              {mwstOption !== 'none' && (
                <div className="form-group">
                  <label className="label">Betrag ist</label>
                  <select
                    className="input"
                    value={amountType}
                    onChange={(e) => setAmountType(e.target.value)}
                  >
                    <option value="gross">Bruttobetrag (inkl. MwSt.)</option>
                    <option value="net">Nettobetrag (exkl. MwSt.)</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Live Totals Preview */}
          {parsedAmount > 0 && (
            <div className="form-section">
              <h3>Zusammenfassung</h3>
              <div className="totals">
                {mwstOption !== 'none' && (
                  <>
                    <div className="total-row">
                      <span>Nettobetrag:</span>
                      <span>€ {netAmount.toFixed(2)}</span>
                    </div>
                    <div className="total-row">
                      <span>MwSt. ({vatRate}%):</span>
                      <span>€ {vatAmount.toFixed(2)}</span>
                    </div>
                  </>
                )}
                <div className="total-row total-row-final">
                  <span>Gesamtbetrag:</span>
                  <span>€ {grossAmount.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          <div className="form-section">
            <h3>Zahlungsstatus</h3>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={alreadyPaid}
                onChange={(e) => setAlreadyPaid(e.target.checked)}
              />
              <span>Bereits bezahlt</span>
            </label>
            {alreadyPaid && (
              <div className="form-row" style={{ marginTop: '12px' }}>
                <div className="form-group">
                  <label className="label">Bezahlt am</label>
                  <input
                    type="date"
                    className="input"
                    value={paidDate}
                    onChange={(e) => setPaidDate(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="label">Zahlungsart</label>
                  <select
                    className="input"
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  >
                    <option value="bar">bar</option>
                    <option value="card">card</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          {buyerIdentificationWarning && (
            <div className="warning" style={{ marginBottom: '15px' }}>
              {buyerIdentificationWarning}
            </div>
          )}

          {error && <div className="error" style={{ marginBottom: '15px' }}>{error}</div>}

          <div className="form-actions">
            <button
              className="btn btn-secondary"
              onClick={() => navigate('/dashboard')}
            >
              Abbrechen
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'Wird erstellt...' : 'Rechnung erstellen'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateInvoice;
